import {
  PrescriptionDocument,
  type PrescriptionDocumentData,
  type PrescriptionLayoutResponse,
} from '@doccareplus/ui/prescription';

interface PortalVitals {
  weight?: number | null;
  weightUnit?: string | null;
  systolic?: number | null;
  diastolic?: number | null;
  temperature?: number | null;
  temperatureUnit?: string | null;
  pulse?: number | null;
}

export interface PortalPrescriptionDocumentValue {
  clinicDisplayName: string;
  patientDisplayName: string;
  providerDisplayName: string;
  signedByDisplayName?: string | null;
  providerQualification?: string | null;
  providerRegistrationNumber?: string | null;
  patientNumber?: string | null;
  patientAge?: number | null;
  patientGender?: string | null;
  visitDate: string;
  chiefComplaint?: string | null;
  diagnosis?: string | null;
  allergySummary?: string | null;
  vitals?: PortalVitals | null;
  investigations: string[];
  items: Array<{
    medicineName: string;
    dose?: string | null;
    frequency?: string | null;
    duration?: string | null;
    instructions?: string | null;
    order: number;
  }>;
  patientAdvice?: string | null;
  followUpDate?: string | null;
}

export function PortalPrescriptionDocument({
  prescription,
  layout,
}: {
  prescription: PortalPrescriptionDocumentValue;
  layout?: PrescriptionLayoutResponse | null;
}) {
  const branding = layout?.branding
    ? {
        ...layout.branding,
        clinicName: layout.branding.clinicName || prescription.clinicDisplayName,
      }
    : undefined;
  const document: PrescriptionDocumentData = {
    provider: {
      name: prescription.providerDisplayName,
      qualification: prescription.providerQualification,
      registrationNumber: prescription.providerRegistrationNumber,
    },
    signedByName: prescription.signedByDisplayName,
    patient: {
      patientNumber: prescription.patientNumber ?? '',
      fullName: prescription.patientDisplayName,
      age: prescription.patientAge,
      gender: prescription.patientGender,
    },
    visitDate: prescription.visitDate,
    allergy: prescription.allergySummary,
    vitals: formatVitals(prescription.vitals),
    complaint: prescription.chiefComplaint,
    diagnosis: prescription.diagnosis,
    investigations: prescription.investigations,
    items: prescription.items,
    advice: prescription.patientAdvice,
    followUpDate: prescription.followUpDate,
  };

  return (
    <div className="overflow-x-auto">
      <PrescriptionDocument
        branding={branding}
        format={layout?.format}
        document={document}
        clinicFallbackName={prescription.clinicDisplayName}
      />
    </div>
  );
}

function formatVitals(vitals: PortalVitals | null | undefined): string[] {
  if (!vitals) return [];
  const values: string[] = [];
  if (vitals.systolic != null && vitals.diastolic != null) {
    values.push(`BP ${vitals.systolic}/${vitals.diastolic}`);
  }
  if (vitals.temperature != null) {
    values.push(`Temp ${vitals.temperature}°${vitals.temperatureUnit ?? 'F'}`);
  }
  if (vitals.pulse != null) values.push(`Pulse ${vitals.pulse}`);
  if (vitals.weight != null) values.push(`Weight ${vitals.weight} ${vitals.weightUnit ?? 'kg'}`);
  return values;
}
